<template>
  <div class="sound-meter" :class="{ 'is-active': effectiveActive }">
    <div class="sound-meter__wave sound-meter__wave--left" aria-hidden="true">
      <i v-for="n in barCount" :key="'l' + n" :style="barStyle(n, 'left')" />
    </div>
    <div class="sound-meter__wave sound-meter__wave--right" aria-hidden="true">
      <i v-for="n in barCount" :key="'r' + n" :style="barStyle(n, 'right')" />
    </div>
    <div class="sound-meter__center">
      <strong v-if="normalized.showNumber" :style="{ color: normalized.numberColor }">{{ displayVolume }}</strong>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SoundWaveMeter',
  props: {
    active: { type: Boolean, default: false },
    config: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      volume: 0,
      visualVolume: 0,
      frame: 0,
      barCount: 28,
      micReady: false,
      localPaused: false,
      micError: '',
      audioContext: null,
      analyser: null,
      stream: null,
      rafId: null,
      samples: null
    }
  },
  computed: {
    normalized() {
      const c = this.config || {}
      const number = (value, fallback, min, max) => {
        const n = Number(value)
        return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : fallback
      }
      return {
        waveColor: c.waveColor || '#ffe58f',
        numberColor: c.numberColor || '#ffffff',
        minDb: number(c.minDb, -60, -100, -10),
        maxDb: number(c.maxDb, -10, -50, 0),
        smoothing: number(c.smoothing, .72, 0, .95),
        showNumber: c.showNumber !== false
      }
    },
    effectiveActive() { return this.active && !this.localPaused && this.micReady },
    displayVolume() { return Math.max(0, Math.min(100, Math.round(this.visualVolume))) },
  },
  watch: {
    active: {
      immediate: true,
      handler(value) {
        if (value) {
          this.localPaused = false
          this.$nextTick(() => this.startCapture())
        } else {
          this.localPaused = false
          this.stopCapture()
        }
      }
    }
  },
  beforeDestroy() {
    this._destroyed = true
    this.stopCapture()
  },
  created() {
    this._destroyed = false
    this._smooth = 0
    this._lastPush = 0
  },
  methods: {
    async startCapture() {
      if (!this.active || this.localPaused || this.stream) return
      this.micError = ''
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.micError = '当前浏览器不支持麦克风'
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } })
        if (this._destroyed || !this.active || this.localPaused) {
          stream.getTracks().forEach(track => track.stop())
          return
        }
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        const context = new AudioCtx()
        if (context.state === 'suspended') await context.resume()
        const analyser = context.createAnalyser()
        analyser.fftSize = 2048
        analyser.smoothingTimeConstant = 0
        context.createMediaStreamSource(stream).connect(analyser)
        this.stream = stream
        this.audioContext = context
        this.analyser = analyser
        this.samples = new Float32Array(analyser.fftSize)
        this.micReady = true
        this.measure()
      } catch (e) {
        this.micReady = false
        this.micError = '请允许麦克风权限'
      }
    },
    measure() {
      if (!this.analyser || !this.effectiveActive) return
      this.analyser.getFloatTimeDomainData(this.samples)
      let sum = 0
      for (let i = 0; i < this.samples.length; i++) sum += this.samples[i] * this.samples[i]
      const rms = Math.sqrt(sum / this.samples.length)
      const db = rms > 0 ? 20 * Math.log10(rms) : this.normalized.minDb
      const range = Math.max(1, this.normalized.maxDb - this.normalized.minDb)
      const next = Math.max(0, Math.min(100, (db - this.normalized.minDb) / range * 100))
      const keep = this.normalized.smoothing
      this._smooth = (this._smooth || 0) * keep + next * (1 - keep)
      // 采样保持 60fps，但只按 ~12fps 推送响应式数值，避免每帧重渲染 56 根柱子
      const now = performance.now()
      if (now - this._lastPush >= 80) {
        this._lastPush = now
        this.visualVolume = this._smooth
        this.volume = next
        this.frame += 1
        this.$emit('volume', this.displayVolume)
      }
      this.rafId = requestAnimationFrame(this.measure)
    },
    stopCapture() {
      if (this.rafId) cancelAnimationFrame(this.rafId)
      this.rafId = null
      if (this.stream) this.stream.getTracks().forEach(track => track.stop())
      if (this.audioContext) {
        try { this.audioContext.close() } catch (e) { /* ignore */ }
      }
      this.stream = null
      this.audioContext = null
      this.analyser = null
      this.samples = null
      this.micReady = false
    },
    barStyle(n, side) {
      const shapeIndex = side === 'left' ? this.barCount - n + 1 : n
      const envelope = .48 + .52 * (1 - (shapeIndex - 1) / Math.max(1, this.barCount - 1))
      const jitter = .72 + .28 * Math.sin((shapeIndex * 1.73) + this.frame * .16)
      const height = Math.max(4, 5 + this.visualVolume * envelope * jitter * .88)
      return { height: height + '%', backgroundColor: this.normalized.waveColor, boxShadow: `0 0 ${Math.max(4, this.visualVolume / 5)}px ${this.normalized.waveColor}`, transform: side === 'left' ? 'scaleX(-1)' : '' }
    }
  }
}
</script>

<style scoped>
.sound-meter{position:relative;width:100%;height:100%;display:flex;align-items:center;justify-content:center;overflow:visible}.sound-meter__wave{position:absolute;top:0;bottom:0;width:34%;display:flex;align-items:center;gap:clamp(1px,.2vw,4px);overflow:hidden}.sound-meter__wave--left{left:0}.sound-meter__wave--right{right:0}.sound-meter__wave i{flex:1 1 0;min-width:1px;border-radius:999px;opacity:.9;transition:height 70ms linear,box-shadow 120ms ease}.sound-meter__center{position:relative;z-index:2;display:flex;align-items:center;justify-content:center;width:22%;min-width:22%;box-sizing:border-box;background:transparent;text-shadow:0 4px 24px rgba(0,0,0,.6)}.sound-meter__center strong{font-size:clamp(58px,8vw,154px);font-weight:800;line-height:.95;font-variant-numeric:tabular-nums;letter-spacing:-.04em}.sound-meter:not(.is-active) .sound-meter__wave i{opacity:.42;box-shadow:none!important}
</style>
